import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ShieldCheck, Download, Trash2, ChevronRight, FileText, ChevronDown, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const LEGAL_LINKS = [
  ['privacy', 'Privacy Policy'],
  ['terms', 'Terms of Service'],
  ['medical', 'Medical Disclaimer'],
  ['ai', 'AI Disclaimer'],
  ['data-sharing', 'Data Sharing'],
  ['data-retention', 'Data Retention'],
  ['contact', 'Privacy Contact'],
];

export function PrivacySecurity() {
  return (
    <div>
      <div className="label-mini px-2 mb-1.5">Privacy & Security</div>
      <div className="regen-card-elevated overflow-hidden">
        <ManageConsent />
        <DownloadData />
        <LegalLinks />
        <DeleteAccount />
      </div>
    </div>
  );
}

function HubRow({ icon: Icon, color, title, sub, right, onClick, testid }) {
  return (
    <div onClick={onClick} data-testid={testid}
         className={`w-full px-4 py-3.5 flex items-center gap-3 border-b border-[var(--c-hair)] last:border-0 ${onClick ? 'cursor-pointer hover:bg-[var(--c-film)]' : ''}`}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}1a` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{title}</div>
        {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
      </div>
      {right}
    </div>
  );
}

function ManageConsent() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => { if (open && !data) api.get('/me/consents').then(r => setData(r.data)); }, [open]);
  const toggle = async (key) => {
    const next = { ...data, current: { ...data.current, [key]: !data.current[key] } };
    setData(next);
    try { await api.put('/me/consents', { [key]: next.current[key] }); toast.success('Saved'); }
    catch { toast.error('Failed to save'); }
  };
  return (
    <>
      <HubRow icon={ShieldCheck} color="#00C8FF" title="Manage Consent" sub="Health data, marketing & notifications"
              right={<ChevronRight className="w-4 h-4 text-muted-foreground" />} onClick={() => setOpen(true)} testid="row-manage-consent" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)]" data-testid="manage-consent-dialog">
          <DialogHeader><DialogTitle>Manage Consent</DialogTitle></DialogHeader>
          {!data ? <div className="py-6 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /></div> : (
            <div className="space-y-1">
              <ConsentToggle label="Health & fitness data" desc="Required to provide personalized coaching. Turning this off limits the app." checked={data.current.health_data} onChange={() => toggle('health_data')} testid="consent-toggle-health" />
              <ConsentToggle label="Marketing emails" desc="Tips, offers and product updates." checked={data.current.marketing} onChange={() => toggle('marketing')} testid="consent-toggle-marketing" />
              <ConsentToggle label="Push notifications" desc="Reminders and coach messages." checked={data.current.push} onChange={() => toggle('push')} testid="consent-toggle-push" />
              <p className="text-[11px] text-muted-foreground pt-3">Accepted policy versions: {Object.entries(data.accepted_policy_versions || {}).map(([k, v]) => `${k} v${v}`).join(' · ') || '—'}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ConsentToggle({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center gap-3 py-3 border-b border-[var(--c-hair)] last:border-0">
      <div className="flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-[11px] text-muted-foreground">{desc}</div>
      </div>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function DownloadData() {
  const [busy, setBusy] = useState(false);
  const download = async (fmt) => {
    setBusy(true);
    try {
      const r = await api.get('/me/export', { params: { format: fmt }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `regen_my_data.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Your ${fmt.toUpperCase()} export has downloaded`);
    } catch { toast.error('Export failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="px-4 py-3.5 border-b border-[var(--c-hair)]">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-[var(--c-green-soft)]"><Download className="w-4 h-4 text-[var(--c-green-ink)]" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Download My Data</div>
          <div className="text-[11px] text-muted-foreground">Get a copy of all your data (GDPR / PDPA)</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={() => download('pdf')} disabled={busy} data-testid="export-pdf-btn" className="flex-1 bg-[var(--c-green)] text-[#081726] font-semibold rounded-xl">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-1.5" /> Download PDF Report</>}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" disabled={busy} data-testid="export-more-btn" className="border-[var(--c-hair)] rounded-xl">More <ChevronDown className="w-3.5 h-3.5 ml-1" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-regen-card border-[var(--c-hair)]">
            <DropdownMenuItem onClick={() => download('csv')} data-testid="export-csv-btn">Download CSV (Excel)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => download('json')} data-testid="export-json-btn">Download JSON</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function LegalLinks() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <>
      <HubRow icon={FileText} color="#7DD3FC" title="Legal & Policies" sub="Privacy, Terms, Disclaimers & more"
              right={<ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />} onClick={() => setOpen(o => !o)} testid="row-legal" />
      {open && (
        <div className="bg-[var(--c-film)]" data-testid="legal-links">
          {LEGAL_LINKS.map(([slug, title]) => (
            <button key={slug} onClick={() => nav(`/legal/${slug}`)} data-testid={`legal-link-${slug}`}
                    className="w-full pl-16 pr-4 py-2.5 flex items-center justify-between text-left border-b border-[var(--c-hair)] last:border-0 hover:bg-[var(--c-film)]">
              <span className="text-sm">{title}</span>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function DeleteAccount() {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.delete('/me/account', { data: { password: pw, confirm } });
      toast.success('Your account has been permanently deleted.');
      setTimeout(() => logout(), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete account');
      setBusy(false);
    }
  };
  return (
    <>
      <HubRow icon={Trash2} color="#FF4D6D" title="Delete Account" sub="Permanently erase your data"
              right={<ChevronRight className="w-4 h-4 text-muted-foreground" />} onClick={() => setOpen(true)} testid="row-delete-account" />
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setPw(''); setConfirm(''); } }}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)]" data-testid="delete-account-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-400"><AlertTriangle className="w-5 h-5" /> Delete Account</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-3 text-xs text-red-300 leading-relaxed">
              This <strong>permanently deletes</strong> your account and all personal data — logs, measurements, photos, chats and AI history. This cannot be undone. Billing records may be retained where required by law.
            </div>
            <div><Label>Confirm your password</Label><Input type="password" value={pw} onChange={e => setPw(e.target.value)} data-testid="delete-password" className="bg-transparent border-[var(--c-hair)]" /></div>
            <div><Label>Type <span className="font-mono text-red-400">DELETE</span> to confirm</Label><Input value={confirm} onChange={e => setConfirm(e.target.value)} data-testid="delete-confirm" placeholder="DELETE" className="bg-transparent border-[var(--c-hair)]" /></div>
            <Button onClick={del} disabled={busy || !pw || confirm.trim().toUpperCase() !== 'DELETE'} data-testid="delete-account-btn"
                    className="w-full bg-red-500 hover:bg-red-600 text-[var(--c-ink)] font-semibold rounded-xl disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Permanently Delete My Account'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
